import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraButton, SpecraDiffText, SpecraSelect, SpecraSpinner, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraFormField, SpecraInlineBanner, SpecraSectionHeader, SpecraSegmentedControl } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { responseDocumentClient } from '../../lib/connect/clients';
import { OfficeEditorFrame } from './OfficeEditorFrame';
import { containerKey, toContainerRef, useDocumentVersions, useVersionViewSession } from './useOfficeWorkspace';
const encode = (s)=>`${s.documentId}|${s.versionId}`;
function decode(value) {
    const at = value.indexOf('|');
    return {
        documentId: value.slice(0, at),
        versionId: value.slice(at + 1)
    };
}
export function groupSections(sections) {
    const out = [];
    for (const section of sections){
        const last = out[out.length - 1];
        if (section.status !== 'equal') out.push({
            kind: 'section',
            section
        });
        else if (last?.kind === 'unchanged') last.count += 1;
        else out.push({
            kind: 'unchanged',
            key: `${section.anchor}|${section.title}`,
            count: 1
        });
    }
    return out;
}
/**
 * Version compare (WRT-31): a section-aware text diff from `CompareVersions`, or
 * two read-only frames side by side. No in-editor compare exists on ONLYOFFICE CE.
 */ export function CompareDialog({ container, currentVersionId, documentId, initialFromVersionId, onOpenChange, open, otherDocuments = [] }) {
    const versions = useDocumentVersions(container, documentId);
    const list = useMemo(()=>(versions.data?.pages ?? []).flatMap((p)=>p.versions), [
        versions.data
    ]);
    const [mode, setMode] = useState('text');
    const [fromPick, setFromPick] = useState(null);
    const [toPick, setToPick] = useState(null);
    // Defaults: from = the previous named version, else the previous version; to = current.
    const defaultFrom = useMemo(()=>{
        if (initialFromVersionId) return initialFromVersionId;
        const older = list.slice(list.findIndex((v)=>v.id === currentVersionId) + 1);
        return (older.find((v)=>v.label) ?? older[0])?.id ?? '';
    }, [
        list,
        currentVersionId,
        initialFromVersionId
    ]);
    const from = fromPick ? decode(fromPick) : defaultFrom ? {
        documentId,
        versionId: defaultFrom
    } : null;
    const to = toPick ? decode(toPick) : currentVersionId ? {
        documentId,
        versionId: currentVersionId
    } : null;
    const same = !!from && !!to && from.documentId === to.documentId && from.versionId === to.versionId;
    const ready = !!from && !!to && !same;
    const options = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            list.map((v)=>/*#__PURE__*/ _jsx("option", {
                    value: encode({
                        documentId,
                        versionId: v.id
                    }),
                    children: `v${v.versionNumber}${v.label ? ` · ${v.label}` : ''}`
                }, v.id)),
            otherDocuments.filter((d)=>d.currentVersionId).map((d)=>/*#__PURE__*/ _jsx("option", {
                    value: encode({
                        documentId: d.documentId,
                        versionId: d.currentVersionId
                    }),
                    children: `${d.label} (current)`
                }, d.documentId))
        ]
    });
    const diff = useQuery({
        queryKey: [
            'officeCompare',
            containerKey(container),
            from,
            to
        ],
        enabled: open && mode === 'text' && ready,
        queryFn: ()=>responseDocumentClient.compareVersions({
                container: toContainerRef(container),
                documentId: from?.documentId ?? '',
                fromVersionId: from?.versionId ?? '',
                toVersionId: to?.versionId ?? '',
                ...to && from && to.documentId !== from.documentId ? {
                    toDocumentId: to.documentId
                } : {}
            }),
        staleTime: 0,
        gcTime: 0,
        retry: false
    });
    const sideOn = open && mode === 'side' && ready;
    const fromView = useVersionViewSession(container, sideOn ? from?.documentId : undefined, from?.versionId ?? null);
    const toView = useVersionViewSession(container, sideOn ? to?.documentId : undefined, to?.versionId ?? null);
    let body;
    if (!from || !to) {
        body = /*#__PURE__*/ _jsx(SpecraText, {
            tone: "muted",
            children: "There is no earlier version to compare yet."
        });
    } else if (same) {
        body = /*#__PURE__*/ _jsx(SpecraText, {
            tone: "muted",
            children: "Pick two different versions to compare."
        });
    } else if (mode === 'side') {
        body = /*#__PURE__*/ _jsxs("div", {
            className: "flex min-h-0 flex-1 flex-col gap-[var(--space-2)]",
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: "Scroll each side separately."
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "grid min-h-96 flex-1 grid-cols-2 gap-[var(--space-3)]",
                    children: [
                        {
                            id: 'office-compare-from',
                            view: fromView,
                            label: 'From'
                        },
                        {
                            id: 'office-compare-to',
                            view: toView,
                            label: 'To'
                        }
                    ].map(({ id, label, view })=>/*#__PURE__*/ _jsx("div", {
                            className: "min-h-0 min-w-0",
                            children: view.data ? /*#__PURE__*/ _jsx(OfficeEditorFrame, {
                                editorId: id,
                                session: view.data
                            }) : view.isError ? /*#__PURE__*/ _jsx(SpecraText, {
                                role: "alert",
                                tone: "danger",
                                children: `The ${label} version didn't load.`
                            }) : /*#__PURE__*/ _jsx(SpecraSpinner, {
                                label: `Loading ${label} version`
                            })
                        }, id))
                })
            ]
        });
    } else if (diff.isError) {
        body = /*#__PURE__*/ _jsx(SpecraInlineBanner, {
            action: /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>void diff.refetch(),
                size: "sm",
                variant: "secondary",
                children: "Retry"
            }),
            role: "alert",
            tone: "danger",
            variant: "framed",
            children: "The comparison didn't load."
        });
    } else if (!diff.data) {
        body = /*#__PURE__*/ _jsx(SpecraSpinner, {
            label: "Comparing versions"
        });
    } else {
        const entries = groupSections(diff.data.sections);
        body = /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col gap-[var(--space-4)]",
            children: [
                diff.data.coarse ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "neutral",
                    variant: "framed",
                    children: "This change is large; showing paragraph-level differences."
                }) : null,
                entries.every((e)=>e.kind === 'unchanged') ? /*#__PURE__*/ _jsx(SpecraText, {
                    tone: "muted",
                    children: "No differences between these versions."
                }) : entries.map((e)=>e.kind === 'unchanged' ? /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: `${e.count} ${e.count === 1 ? 'section' : 'sections'} unchanged`
                    }, `unchanged-${e.key}`) : /*#__PURE__*/ _jsxs("section", {
                        className: "flex flex-col gap-[var(--space-1)]",
                        "data-testid": Testids.officeWorkspace.compareSection,
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                title: e.section.title,
                                variant: "group"
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                tone: "muted",
                                children: `+${e.section.wordsAdded} words · −${e.section.wordsRemoved} words`
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                children: /*#__PURE__*/ _jsx(SpecraDiffText, {
                                    segments: e.section.segments.map((s)=>({
                                            op: s.op,
                                            text: s.text
                                        }))
                                })
                            })
                        ]
                    }, `${e.section.anchor}|${e.section.title}`))
            ]
        });
    }
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        description: "See what changed between two versions.",
        footer: /*#__PURE__*/ _jsx(SpecraButton, {
            onClick: ()=>onOpenChange(false),
            size: "sm",
            variant: "ghost",
            children: "Close"
        }),
        onOpenChange: onOpenChange,
        open: open,
        scrollBody: true,
        size: "xl",
        testId: Testids.officeWorkspace.compareDialog,
        title: "Compare versions",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex min-h-0 flex-col gap-[var(--space-4)]",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-wrap items-end gap-[var(--space-3)]",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "From",
                            children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                "data-testid": Testids.officeWorkspace.compareFrom,
                                id: "office-compare-from-select",
                                onChange: (e)=>setFromPick(e.target.value),
                                size: "sm",
                                value: from ? encode(from) : '',
                                children: [
                                    from ? null : /*#__PURE__*/ _jsx("option", {
                                        value: "",
                                        children: "—"
                                    }),
                                    options
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "To",
                            children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                "data-testid": Testids.officeWorkspace.compareTo,
                                id: "office-compare-to-select",
                                onChange: (e)=>setToPick(e.target.value),
                                size: "sm",
                                value: to ? encode(to) : '',
                                children: [
                                    to ? null : /*#__PURE__*/ _jsx("option", {
                                        value: "",
                                        children: "—"
                                    }),
                                    options
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                            label: "Compare view",
                            onValueChange: setMode,
                            options: [
                                {
                                    value: 'text',
                                    label: 'Text changes'
                                },
                                {
                                    value: 'side',
                                    label: 'Side by side'
                                }
                            ],
                            size: "sm",
                            testId: Testids.officeWorkspace.compareMode,
                            value: mode
                        })
                    ]
                }),
                body
            ]
        })
    });
}
